import {
  AnimalScreeningReportVendors,
  ApplicationStatus,
  PetScreeningTrigger,
  VerificationStatus
} from "shared/types";
import {
  OurPetPolicyAnimal,
  OurPetPolicyLandlordStatus,
  OurPetPolicyStatus
} from "./types/our-pet-policy";
import { PetScreeningAnimal, PetScreeningStatus } from "./types/pet-screening";
import { BadgeVariant } from "shared/components";

type AnimalScreeningReportAnimals = (OurPetPolicyAnimal | PetScreeningAnimal)[];

type AnimalScreeningRawResponse = {
  animals?: AnimalScreeningReportAnimals;
  pets?: AnimalScreeningReportAnimals;
};

type AnimalScreeningVerificationLike = {
  data?: {
    raw_response?: AnimalScreeningRawResponse | null;
    verification_status?: VerificationStatus | string;
  } | null;
  finished_at?: string | null;
  report?: {
    pets?: AnimalScreeningReportAnimals;
  } | null;
  status?: VerificationStatus | string;
};

export const getVerificationStatusByVendor = (
  pet: PetScreeningAnimal | OurPetPolicyAnimal,
  vendor: AnimalScreeningReportVendors
  // eslint-disable-next-line sonarjs/function-return-type -- polymorphism
): PetScreeningStatus | OurPetPolicyLandlordStatus => {
  if (vendor === AnimalScreeningReportVendors.PetScreening)
    return (pet as PetScreeningAnimal).status;
  return (pet as OurPetPolicyAnimal).verification_status;
};

export const statusBadgeVariant = (
  status: OurPetPolicyStatus | PetScreeningStatus
): BadgeVariant => {
  switch (status) {
    case OurPetPolicyStatus.PENDING:
    case PetScreeningStatus.draft:
    case PetScreeningStatus.verifying:
    case PetScreeningStatus.non_responsive:
    case PetScreeningStatus.pending:
      return BadgeVariant.WARNING;
    case OurPetPolicyStatus.APPROVED:
    case PetScreeningStatus.active:
    case PetScreeningStatus.recommended:
    case PetScreeningStatus.conditionally_recommended:
      return BadgeVariant.SUCCESS;
    case OurPetPolicyStatus.NOT_APPROVED:
    case PetScreeningStatus.declined:
    case PetScreeningStatus.expired:
    case PetScreeningStatus.returned:
      return BadgeVariant.ERROR;
    default:
      return BadgeVariant.BASE;
  }
};

export const getAnimalScreeningRawResponseAnimals = (
  verification?: AnimalScreeningVerificationLike | null
) => {
  const rawResponse = verification?.data?.raw_response;

  return rawResponse?.pets?.length ? rawResponse.pets : rawResponse?.animals;
};

export const hasCompletedAnimalScreeningReport = (
  verification?: AnimalScreeningVerificationLike | null
) => {
  const rawResponseAnimals = getAnimalScreeningRawResponseAnimals(verification);

  return Boolean(
    verification?.status === VerificationStatus.Finished ||
    verification?.status === VerificationStatus.Completed ||
    verification?.finished_at ||
    verification?.data?.verification_status === VerificationStatus.Completed ||
    verification?.report?.pets?.length ||
    rawResponseAnimals?.length
  );
};

/**
 * Where an animal screening request stands before any report data exists.
 *
 * A request configured to go out on approval is not pending work while the
 * application is still awaiting a decision, and is never going out at all once the
 * application is declined — showing either as in progress reads as a stuck report.
 */
export enum AnimalScreeningRequestState {
  /** Invite goes out on submission; the applicant has not submitted yet. */
  AwaitingSubmission = "awaiting_submission",
  /** Invite goes out on approval; no decision has been made yet. */
  AwaitingApproval = "awaiting_approval",
  /** Invite goes out on approval and the application was declined, so it never will. */
  NotNeeded = "not_needed",
  /**
   * The triggering milestone has already passed but no invite was ever sent — the
   * applicant is manual, screening is off, or the screening URL is blank. Promising
   * that it "starts once submitted/approved" would be a lie.
   */
  NotRequested = "not_requested",
  /** The invite went out, then the application was declined and it was called off. */
  Canceled = "canceled",
  /** The invite has gone out; the report is genuinely pending. */
  Requested = "requested"
}

type AnimalScreeningRequestStateInput = {
  verificationStatus?: VerificationStatus | string;
  requestedAt?: string | null;
  triggerOn?: PetScreeningTrigger;
  applicationStatus?: ApplicationStatus;
};

const DECLINED_APPLICATION_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Rejected
];

/** Decided one way or the other — the approval milestone can no longer arrive. */
const DECIDED_APPLICATION_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Approved,
  ApplicationStatus.ConditionalApproved,
  ApplicationStatus.Rejected,
  ApplicationStatus.Canceled,
  ApplicationStatus.Merged
];

/** The applicant is past the point where a submission would still trigger an invite. */
const SUBMITTED_APPLICATION_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Submitted,
  ...DECIDED_APPLICATION_STATUSES
];

const isDeclined = (status?: ApplicationStatus) =>
  Boolean(status && DECLINED_APPLICATION_STATUSES.includes(status));

/*
 * The only verification statuses that can still mean "nothing has happened yet".
 * Anything else — failed, processing, finished — is an outcome, and an outcome is
 * worth showing even when `requested_at` is empty: the PetScreening webhook and the
 * OurPetPolicy sync both move the status without ever setting it.
 */
const PRE_REQUEST_VERIFICATION_STATUSES: (VerificationStatus | string)[] = [
  VerificationStatus.Pending,
  VerificationStatus.Started,
  VerificationStatus.Skipped
];

export const getAnimalScreeningRequestState = ({
  verificationStatus,
  requestedAt,
  triggerOn,
  applicationStatus
}: AnimalScreeningRequestStateInput): AnimalScreeningRequestState => {
  /*
   * Before the requested_at check, not after: a cancelled verification still carries
   * the timestamp of the invite that did go out, so testing requested_at first would
   * report it as pending forever. The backend only cancels on a decline, so this needs
   * no trigger or application-status qualification -- it reads the same either way.
   */
  if (verificationStatus === VerificationStatus.Canceled)
    return AnimalScreeningRequestState.Canceled;

  if (requestedAt) return AnimalScreeningRequestState.Requested;

  if (
    verificationStatus &&
    !PRE_REQUEST_VERIFICATION_STATUSES.includes(verificationStatus)
  )
    return AnimalScreeningRequestState.Requested;

  /*
   * The backend closes these out explicitly on decline, so trust the flag — but only
   * where it can still be true. Undo Decision puts a declined application back under
   * review, and the skip is not guaranteed to have been cleared yet, so a known
   * non-declined status wins over a stale flag.
   */
  if (verificationStatus === VerificationStatus.Skipped)
    return !applicationStatus || isDeclined(applicationStatus)
      ? AnimalScreeningRequestState.NotNeeded
      : AnimalScreeningRequestState.AwaitingApproval;

  if (triggerOn === PetScreeningTrigger.OnApproval) {
    if (isDeclined(applicationStatus))
      return AnimalScreeningRequestState.NotNeeded;

    return applicationStatus &&
      DECIDED_APPLICATION_STATUSES.includes(applicationStatus)
      ? AnimalScreeningRequestState.NotRequested
      : AnimalScreeningRequestState.AwaitingApproval;
  }

  return applicationStatus &&
    SUBMITTED_APPLICATION_STATUSES.includes(applicationStatus)
    ? AnimalScreeningRequestState.NotRequested
    : AnimalScreeningRequestState.AwaitingSubmission;
};
